import { Fade } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotatingLines } from 'react-loader-spinner';

import AnimatedDots from '../animated-dots/AnimatedDots';

import './TextLoader.scss';

const DOTS_DURATION = 1500;

function TextLoader({ label, labelsArray, isLoading, disableScrolling, scrollableRef, timeout = 8000 }) {
  const { t } = useTranslation();

  const [fixedTop, setFixedTop] = useState(0);
  const [currentLabel, setCurrentLabel] = useState(label);
  const [showDots, setShowDots] = useState(false);

  const autoLabelChange = (labels) => {
    let index = 0;

    setCurrentLabel(labels[index]);
    setShowDots(false);

    const interval = setInterval(() => {
      setShowDots(true);

      index = (index + 1) % labels.length;
      setCurrentLabel(labels[index]);

      setTimeout(() => {
        setShowDots(false);
      }, 2000);
    }, timeout + DOTS_DURATION);

    return () => clearInterval(interval);
  };
  useEffect(() => {
    if (!isLoading) {
      return;
    }

    if (labelsArray?.length) {
      const cleanup = autoLabelChange(labelsArray);
      return cleanup;
    } else {
      setCurrentLabel(label || t('loading'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, labelsArray, isLoading]);

  useEffect(() => {
    if (!disableScrolling || !scrollableRef) {
      return;
    }
    const scrollableContainer = scrollableRef?.current;

    const scrollTop = scrollableContainer.scrollTop;
    setFixedTop(scrollTop ?? 0);

    const handlePreventParentScroll = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    if (isLoading) {
      scrollableContainer.addEventListener('wheel', handlePreventParentScroll, {
        passive: false,
      });
      scrollableContainer.addEventListener('touchmove', handlePreventParentScroll, {
        passive: false,
      });
    } else {
      scrollableContainer.removeEventListener('wheel', handlePreventParentScroll, {
        passive: false,
      });
      scrollableContainer.removeEventListener('touchmove', handlePreventParentScroll, {
        passive: false,
      });
    }

    return () => {
      scrollableContainer.removeEventListener('wheel', handlePreventParentScroll, {
        passive: false,
      });
      scrollableContainer.removeEventListener('touchmove', handlePreventParentScroll, {
        passive: false,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, disableScrolling]);

  const fixedStyles = useMemo(
    () => ({
      top: `${fixedTop}px`,
    }),
    [fixedTop],
  );

  return (
    <Fade in={isLoading}>
      <div className='text-loader_overlay' style={fixedStyles}>
        <div className='text-loader_dots'>
          <RotatingLines strokeColor='#fdbf47' strokeWidth={5} animationDuration='0.75' width={50} visible={true} />
        </div>
        {showDots ? <AnimatedDots size='12px' /> : <span className='text-loader_label'>{currentLabel}</span>}
      </div>
    </Fade>
  );
}

export default TextLoader;
